import { useState } from 'react'
import WireframeLayout, { buildSidebarSections } from '@/components/WireframeLayout'
import { cn, Badge, Card, Button, Progress, Separator, AIReasoning, KPI } from '../components/primitives'
import { POS } from '../components/data'

function OpsView() {
  const [er, setEr] = useState<Record<string, boolean>>({});
  const [ps, setPs] = useState<Record<string, string>>({});
  const tog = (k: string) => setEr(p => ({ ...p, [k]: !p[k] }));
  const prioClr: Record<string, string> = { urgent: "#ef4444", high: "#f97316", medium: "#f59e0b", standard: "#94a3b8" };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-3 flex-wrap">
        <KPI label="Pending Approvals" value="3" delta="2 urgent" dir="down" />
        <KPI label="Open POs" value="7" sub="$271K total value" />
        <KPI label="Supplier On-Time" value="91%" delta="-3% vs target" dir="down" />
        <KPI label="Exceptions" value="5" sub="2 resolved · 3 active" />
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-slate-900">Purchase Plans — Review & Approve</h3>
          <div className="flex gap-2"><Button variant="outline" size="sm">Filter</Button><Button variant="outline" size="sm">Export</Button></div>
        </div>
        <div className="flex flex-col gap-3">
          {POS.map(po => {
            const ds = ps[po.id] || po.status;
            return (
              <Card key={po.id} style={{ borderLeft: `3px solid ${prioClr[po.prio]}` }}>
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-bold text-slate-900 font-mono">{po.id}</span>
                        <Badge variant={po.prio}>{po.prio}</Badge>
                        <Badge variant={ds}>{ds}</Badge>
                      </div>
                      <p className="text-xs text-slate-500">{po.sup} · {po.skus.join(", ")} · {po.units.toLocaleString()} units</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-slate-900">${(po.cost / 1000).toFixed(1)}K</div>
                      <div className="text-xs text-slate-400">ETA: {po.eta}</div>
                    </div>
                  </div>
                  <AIReasoning text={po.reasoning} open={er[po.id]} toggle={() => tog(po.id)} />
                  {ds === "pending" && (
                    <>
                      <Separator className="my-3" />
                      <div className="flex gap-2">
                        <Button variant="success" size="sm" onClick={() => setPs(p => ({ ...p, [po.id]: "approved" }))}>✓ Approve</Button>
                        <Button variant="destructive" size="sm" onClick={() => setPs(p => ({ ...p, [po.id]: "draft" }))}>✕ Reject</Button>
                        <Button variant="outline" size="sm">Edit Constraints</Button>
                        <Button variant="outline" size="sm">Defer</Button>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Supplier Constraints & MOQs</h3>
        <Card className="divide-y divide-slate-100">
          {[
            { name: "Guangzhou Beauty Co.", moq: 1000, lt: "12-15d", rel: 94, cap: 80, slot: "Mar 10" },
            { name: "Shanghai Cosmetics Ltd.", moq: 2000, lt: "10-12d", rel: 88, cap: 65, slot: "Mar 8" },
            { name: "Korea Ingredients Inc.", moq: 500, lt: "18-21d", rel: 96, cap: 45, slot: "Mar 14" },
          ].map((s, i) => (
            <div key={i} className="p-4 flex justify-between items-center">
              <div>
                <div className="text-sm font-semibold text-slate-900">{s.name}</div>
                <div className="text-xs text-slate-400 mt-0.5">MOQ: {s.moq} · Lead: {s.lt} · Next: {s.slot}</div>
              </div>
              <div className="flex items-center gap-5">
                <div className="text-center"><div className="text-xs text-slate-400">Reliability</div><div className={cn("text-sm font-bold", s.rel > 90 ? "text-emerald-600" : "text-amber-600")}>{s.rel}%</div></div>
                <div className="w-16"><div className="text-xs text-slate-400 mb-1">Capacity</div><Progress value={s.cap} color={s.cap > 75 ? "#f59e0b" : "#10b981"} /></div>
                <Button variant="outline" size="sm">Edit</Button>
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

export default function Dashboard({ role }: { role: string }) {
  return (
    <WireframeLayout
      title="Operations Workflow"
      subtitle="Review, approve & manage exceptions"
      titleIcon="⚙"
      titleColor="#f97316"
      sidebarLinks={buildSidebarSections(role, "dashboard")}
    >
      <OpsView />
    </WireframeLayout>
  )
}
